import { useMemo, useState, type ReactNode } from 'react';

export interface Column<T> {
  key: string;
  title: string;
  render: (row: T) => ReactNode;
  /** value used for client-side sorting; omit for unsortable columns */
  sortValue?: (row: T) => string | number | null;
  /** marks a column sortable in controlled mode (the caller sorts, e.g. on the server) */
  sortable?: boolean;
  wrap?: boolean;
}

export interface TableSort {
  key: string;
  dir: 'asc' | 'desc';
}

/**
 * Registry pattern "table": sortable columns, sticky header, horizontal scroll on narrow screens.
 * Uncontrolled: rows are sorted client-side by `sortValue`. Controlled (`sort` + `onSort`): the header only
 * reports the requested order and the caller sorts — mandatory for paginated lists, where sorting one page is a lie.
 */
export function Table<T>({ columns, rows, rowKey, empty, sort, onSort, defaultSort }: { columns: Column<T>[]; rows: T[]; rowKey: (row: T) => string; empty: string; sort?: TableSort | null; onSort?: (sort: TableSort | null) => void; /** uncontrolled mode: the order shown before the user clicks a header */ defaultSort?: TableSort }) {
  const controlled = onSort !== undefined;
  const [localSort, setLocalSort] = useState<TableSort | null>(defaultSort ?? null);
  const current = controlled ? (sort ?? null) : localSort;

  const sorted = useMemo(() => {
    if (controlled || !current) return rows;
    const col = columns.find((c) => c.key === current.key);
    if (!col?.sortValue) return rows;
    const sv = col.sortValue;
    return [...rows].sort((a, b) => {
      const va = sv(a);
      const vb = sv(b);
      if (va === vb) return 0;
      if (va === null) return 1;
      if (vb === null) return -1;
      const cmp = typeof va === 'number' && typeof vb === 'number' ? va - vb : String(va).localeCompare(String(vb), 'uk');
      return current.dir === 'asc' ? cmp : -cmp;
    });
  }, [rows, current, columns, controlled]);

  const next = (key: string): TableSort | null => (current?.key === key ? (current.dir === 'asc' ? { key, dir: 'desc' } : null) : { key, dir: 'asc' });
  const toggle = (key: string) => (controlled ? onSort(next(key)) : setLocalSort(next(key)));
  const isSortable = (c: Column<T>) => (controlled ? !!c.sortable || !!c.sortValue : !!c.sortValue);

  return (
    <div className="table-wrap">
      <table className="table">
        <thead>
          <tr>
            {columns.map((c) => (
              <th key={c.key} scope="col">
                {isSortable(c) ? (
                  <button type="button" onClick={() => toggle(c.key)} data-dir={current?.key === c.key ? current.dir : undefined}>
                    {c.title}
                  </button>
                ) : (
                  c.title
                )}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {sorted.length === 0 && (
            <tr>
              <td colSpan={columns.length} className="muted wrap">{empty}</td>
            </tr>
          )}
          {sorted.map((row) => (
            <tr key={rowKey(row)}>
              {columns.map((c) => (
                <td key={c.key} className={c.wrap ? 'wrap' : undefined}>{c.render(row)}</td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
